<template>
  <div>
    <v-row>
      <v-col cols="12" md="12">
        <v-card class="mx-auto" outlined>
          <v-card-title>
            Sammelvertragpreise
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12" md="6">
                <v-select
                  v-model="selectedVertrag"
                  :items="availableVertraege"
                  label="Sammelvertrag auswählen"
                  @change="loadPriceData"
                ></v-select>
              </v-col>
              <v-col cols="12" md="6" class="d-flex align-center justify-end">
                <v-btn
                  color="primary"
                  @click="exportToCSV"
                  :disabled="!priceData || priceData.length === 0"
                >
                  <v-icon left>mdi-download</v-icon>
                  Als CSV exportieren
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row v-if="loading" class="mt-4">
      <v-col cols="12">
        <v-progress-linear indeterminate></v-progress-linear>
      </v-col>
    </v-row>

    <v-row v-if="priceData && priceData.length > 0" class="mt-4">
      <v-col cols="12" md="12">
        <v-card class="mx-auto" outlined>
          <v-card-title>
            Preisdaten Tabelle
            <v-spacer></v-spacer>
            <v-text-field
              v-model="tableSearch"
              append-icon="mdi-magnify"
              label="Suche"
              single-line
              hide-details
            ></v-text-field>
          </v-card-title>
          <v-data-table
            :headers="tableHeaders"
            :items="priceData"
            :items-per-page="25"
            :search="tableSearch"
            class="elevation-1"
          >
            <template v-slot:item.formattedDate="{ item }">
              {{ formatTimestampToDate(item.timestamp) }}
            </template>
            <template v-slot:item.lastValue="{ item }">
              {{ formatPrice(item.lastValue) }}
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <v-row v-if="priceData && priceData.length > 0" class="mt-4">
      <v-col cols="12" md="12">
        <v-card class="mx-auto" outlined>
          <v-card-title>
            Preisdiagramm
          </v-card-title>
          <v-card-text>
            <evchart
              v-if="chartData.datasets.length > 0"
              :chartOptions="chartOptions"
              :chartData="chartData"
            ></evchart>
            <div v-else class="text-center pa-4">
              Keine Daten verfügbar
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row v-if="errorMessage" class="mt-4">
      <v-col cols="12">
        <v-alert type="error" dismissible @input="errorMessage = ''">
          {{ errorMessage }}
        </v-alert>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import axios from "axios";
import evchart from "@/components/evchart.vue";

export default {
  name: "Sammelvertragspreise",
  components: {
    evchart
  },
  data() {
    return {
      selectedVertrag: null,
      priceData: [],
      loading: false,
      tableSearch: "",
      errorMessage: "",
      tableHeaders: [
        { text: "Datum & Uhrzeit", value: "formattedDate" },
        { text: "Timestamp", value: "timestamp" },
        { text: "Preis (€/MWh)", value: "lastValue" }
      ],
      chartOptions: {
        responsive: true,
        maintainAspectRatio: true,
        interaction: {
          intersect: false,
          mode: "index"
        },
        plugins: {
          legend: {
            display: true,
            position: "top"
          },
          tooltip: {
            backgroundColor: "rgba(0,0,0,0.8)",
            titleColor: "#fff",
            bodyColor: "#fff",
            borderColor: "#ddd",
            borderWidth: 1,
            displayColors: true,
            padding: 15,
            caretPadding: 15,
            titleFont: {
              size: 13,
              weight: "bold"
            },
            callbacks: {
              label: function(context) {
                let label = context.dataset.label || "";
                if (label) {
                  label += ": ";
                }
                if (context.parsed.y !== null) {
                  label += parseFloat(context.parsed.y).toLocaleString("de-DE", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  });
                }
                return label;
              }
            }
          }
        },
        scales: {
          y: {
            beginAtZero: false,
            title: {
              display: true,
              text: "Preis (€/MWh)"
            },
            ticks: {
              callback: function(value) {
                return parseFloat(value).toLocaleString("de-DE", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                });
              }
            }
          },
          x: {
            type: "time",
            time: {
              unit: "day",
              displayFormats: {
                day: "dd.MM.yyyy"
              }
            },
            title: {
              display: true,
              text: "Datum"
            }
          }
        }
      }
    };
  },
  computed: {
    availableVertraege() {
      if (!this.$store.state.vertraege || !this.$store.state.vertraege.vertraege) {
        return [];
      }
      return Object.keys(this.$store.state.vertraege.vertraege).filter(vertrag => {
        const vertragsObj = this.$store.state.vertraege.vertraege[vertrag];
        return vertragsObj.konfiguriert;
      });
    },
    chartData() {
      if (!this.priceData || this.priceData.length === 0) {
        return {
          labels: [],
          datasets: []
        };
      }

      const labels = this.priceData.map(entry => new Date(entry.timestamp * 1000));
      const prices = this.priceData.map(entry => parseFloat(entry.lastValue));

      return {
        labels: labels,
        datasets: [
          {
            label: this.selectedVertrag || "Preis",
            borderColor: "#f87979",
            backgroundColor: "rgba(248, 121, 121, 0.1)",
            borderWidth: 2,
            pointRadius: 4,
            pointBackgroundColor: "#f87979",
            pointBorderColor: "#fff",
            pointBorderWidth: 2,
            pointHoverRadius: 6,
            tension: 0.1,
            data: prices,
            fill: true
          }
        ]
      };
    }
  },
  methods: {
    async loadPriceData() {
      if (!this.selectedVertrag) {
        this.priceData = [];
        this.errorMessage = "";
        return;
      }

      this.loading = true;
      this.errorMessage = "";
      
      try {
        const response = await axios.post(
          "https://europe-west3-energyview-test.cloudfunctions.net/getChartData",
          {
            data: {
              vertrag: this.selectedVertrag
            }
          }
        );

        if (response.data && response.data.result && Array.isArray(response.data.result)) {
          this.priceData = response.data.result.sort((a, b) => a.timestamp - b.timestamp);
        } else {
          this.errorMessage = "Keine Daten vom Server erhalten";
          this.priceData = [];
        }
      } catch (error) {
        console.error("Fehler beim Laden der Preisdaten:", error);
        this.errorMessage = `Fehler beim Laden der Preisdaten: ${error.message}`;
        this.priceData = [];
      } finally {
        this.loading = false;
      }
    },
    formatTimestampToDate(timestamp) {
      const date = new Date(timestamp * 1000);
      return date.toLocaleString("de-DE", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit"
      });
    },
    formatPrice(price) {
      const num = parseFloat(price);
      return num.toLocaleString("de-DE", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },
    exportToCSV() {
      if (!this.priceData || this.priceData.length === 0) {
        return;
      }

      // Header
      const headers = ["Datum & Uhrzeit", "Timestamp", "Preis (€/MWh)"];
      const csvContent = [
        headers.join(";"),
        ...this.priceData.map(row =>
          [
            this.formatTimestampToDate(row.timestamp),
            row.timestamp,
            row.lastValue
          ].join(";")
        )
      ].join("\n");

      // CSV-Datei erstellen und herunterladen
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);

      const filename = `Preisdaten_${this.selectedVertrag}_${new Date().toISOString().split("T")[0]}.csv`;
      link.setAttribute("href", url);
      link.setAttribute("download", filename);
      link.style.visibility = "hidden";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  },
  async mounted() {
    // SignIn Check
    if (!this.$store.state.auth.isSignedIn) {
      this.$router.push("/login");
      return;
    }

    // Verträge immer laden
    try {
      await this.$store.dispatch("attachVertraege");
    } catch (error) {
      console.error("Fehler beim Laden der Verträge:", error);
      this.errorMessage = "Verträge konnten nicht geladen werden";
    }
  }
};
</script>

<style scoped>
</style>
